// CB:BEGIN pm-documents-page
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getApiUrl } from '../lib/getApiUrl';
import SharedHeaderPropertyManager from '../components/SharedHeaderPropertyManager';
import ContentLimiter from '../components/layout/ContentLimiter';
import { FileText, ChevronDown, ChevronRight, FolderOpen, Download, Trash2, Upload } from 'lucide-react';

const BRAND_GREEN = '#123c3b';

interface Property {
  id: string;
  label?: string;
  address1: string;
  city: string;
  state: string;
  zip?: string;
  projects?: Project[];
}

interface Project {
  id: string;
  address: string;
  projectType: string;
  status: string;
  createdAt: string;
}

interface Doc {
  id: string;
  documentType: string;
  documentCategory?: string;
  visibility?: string;
  s3Key?: string;
  createdAt: string;
  metadata?: any;
}

interface PropertyWithDocs {
  property: Property;
  propertyDocs: Doc[];
  projectDocs: { project: Project; docs: Doc[] }[];
  loading: boolean;
  expanded: boolean;
}

const DOC_TYPE_LABELS: Record<string, string> = {
  PROPERTY_DOCUMENT: 'Property Document',
  INSURANCE_DOCUMENT: 'Insurance',
  PERMIT: 'Permit',
  WARRANTY: 'Warranty',
  CHANGE_ORDER: 'Change Order',
  FINAL_COMPLETION: 'Final Completion',
  SIGNED_CONTRACT: 'Signed Contract',
  MILESTONE_1_EVIDENCE: 'Milestone 1 Evidence',
  MILESTONE_2_EVIDENCE: 'Milestone 2 Evidence',
  MILESTONE_3_EVIDENCE: 'Milestone 3 Evidence',
};

function docLabel(d: Doc): string {
  if (d.metadata?.docLabel) return d.metadata.docLabel;
  return DOC_TYPE_LABELS[d.documentType] || d.documentType.replace(/_/g, ' ');
}

function fmtDate(s: string) {
  try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return s; }
}

interface Props { onNavigate: (screen: string) => void; }

export default function PropertyManagerDocumentsPage({ onNavigate }: Props) {
  const { getToken } = useAuth();
  const [entries, setEntries] = useState<PropertyWithDocs[]>([]);
  const [loading, setLoading] = useState(true);

  // CB:BEGIN pm-doc-download
  const handleDownload = useCallback(async (docId: string, kind: 'property' | 'project', propertyId?: string) => {
    try {
      const token = await getToken().catch(() => null);
      if (!token) return;
      const endpoint = kind === 'property'
        ? getApiUrl(`/api/property-manager/properties/${propertyId}/documents/${docId}/download`)
        : getApiUrl(`/api/documents/${docId}/url`);
      const r = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' });
      if (!r.ok) { alert('Could not generate download link. Please try again.'); return; }
      const d = await r.json();
      if (d.url) window.open(d.url, '_blank', 'noopener,noreferrer');
    } catch { alert('Download failed. Please check your connection and try again.'); }
  }, [getToken]);
  // CB:END pm-doc-download

  const fetchProperties = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken().catch(() => null);
      if (!token) { setLoading(false); return; }
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(getApiUrl('/api/property-manager/properties'), { headers, credentials: 'include' });
      if (!res.ok) { setLoading(false); return; }
      const data = await res.json();
      const props: Property[] = data.properties || [];
      setEntries(props.map(p => ({
        property: p,
        propertyDocs: [],
        projectDocs: [],
        loading: false,
        expanded: false,
      })));
    } finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => { fetchProperties(); }, [fetchProperties]);

  const toggleExpand = useCallback(async (propertyId: string, currentlyExpanded: boolean) => {
    if (currentlyExpanded) {
      setEntries(prev => prev.map(e => e.property.id === propertyId ? { ...e, expanded: false } : e));
      return;
    }

    // Mark loading + expanded
    setEntries(prev => prev.map(e => e.property.id === propertyId ? { ...e, expanded: true, loading: true } : e));

    try {
      const token = await getToken().catch(() => null);
      if (!token) return;
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch property docs + property detail (includes projects)
      const [propDocRes, propDetailRes] = await Promise.all([
        fetch(getApiUrl(`/api/property-manager/properties/${propertyId}/documents`), { headers, credentials: 'include' }),
        fetch(getApiUrl(`/api/property-manager/properties/${propertyId}`), { headers, credentials: 'include' }),
      ]);

      const propertyDocs: Doc[] = propDocRes.ok ? (await propDocRes.json()).documents || [] : [];
      const propDetail = propDetailRes.ok ? await propDetailRes.json() : null;
      const projects: Project[] = propDetail?.property?.projects || [];

      // Fetch docs for each project in parallel
      const projectDocResults = await Promise.all(
        projects.map(async (proj) => {
          const r = await fetch(getApiUrl(`/api/projects/${proj.id}/documents`), { headers, credentials: 'include' });
          const docs: Doc[] = r.ok ? (await r.json()).documents || [] : [];
          return { project: proj, docs };
        })
      );

      setEntries(prev => prev.map(e =>
        e.property.id === propertyId
          ? { ...e, propertyDocs, projectDocs: projectDocResults, loading: false }
          : e
      ));
    } catch {
      setEntries(prev => prev.map(e => e.property.id === propertyId ? { ...e, loading: false } : e));
    }
  }, [getToken]);

  const handleDelete = useCallback(async (propertyId: string, docId: string) => {
    if (!confirm('Delete this document?')) return;
    try {
      const token = await getToken().catch(() => null);
      if (!token) return;
      await fetch(getApiUrl(`/api/property-manager/properties/${propertyId}/documents/${docId}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      // Refresh this property's docs
      setEntries(prev => prev.map(e =>
        e.property.id === propertyId
          ? { ...e, propertyDocs: e.propertyDocs.filter(d => d.id !== docId) }
          : e
      ));
    } catch { /* ignore */ }
  }, [getToken]);

  const totalDocs = entries.reduce((sum, e) => sum + e.propertyDocs.length + e.projectDocs.reduce((s, pd) => s + pd.docs.length, 0), 0);

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <SharedHeaderPropertyManager onNavigate={onNavigate} activeScreen="pm-documents" />

      <ContentLimiter>
        <div style={{ padding: '32px 0' }}>
          {/* Header */}
          <div style={{ marginBottom: 28 }}>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: 0 }}>Documents</h1>
            <p style={{ fontSize: 13, color: '#64748b', marginTop: 6, margin: 0 }}>
              Property-level and project-level documents across your portfolio.
              {totalDocs > 0 && ` ${totalDocs} document${totalDocs !== 1 ? 's' : ''} loaded.`}
            </p>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: 14 }}>Loading properties…</div>
          ) : entries.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '60px 32px', textAlign: 'center' }}>
              <FolderOpen size={40} style={{ color: '#cbd5e1', marginBottom: 12 }} />
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>No properties yet</div>
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>Add properties from your dashboard to manage documents here.</div>
              <button onClick={() => onNavigate('pm-dashboard')} style={{ padding: '10px 24px', background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Go to Dashboard</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {entries.map(entry => (
                <PropertyDocRow
                  key={entry.property.id}
                  entry={entry}
                  onToggle={() => toggleExpand(entry.property.id, entry.expanded)}
                  onDeletePropertyDoc={(docId) => handleDelete(entry.property.id, docId)}
                  onDownloadPropertyDoc={(docId) => handleDownload(docId, 'property', entry.property.id)}
                  onDownloadProjectDoc={(docId) => handleDownload(docId, 'project')}
                  onNavigateToProperty={() => onNavigate(`/pm/properties/${entry.property.id}`)}
                  onNavigateToProject={(projectId) => onNavigate(`/pm/projects/${projectId}`)}
                />
              ))}
            </div>
          )}
        </div>
      </ContentLimiter>
    </div>
  );
}

interface RowProps {
  entry: PropertyWithDocs;
  onToggle: () => void;
  onDeletePropertyDoc: (docId: string) => void;
  onDownloadPropertyDoc: (docId: string) => void;
  onDownloadProjectDoc: (docId: string) => void;
  onNavigateToProperty: () => void;
  onNavigateToProject: (projectId: string) => void;
}

function PropertyDocRow({ entry, onToggle, onDeletePropertyDoc, onDownloadPropertyDoc, onDownloadProjectDoc, onNavigateToProperty, onNavigateToProject }: RowProps) {
  const { property, propertyDocs, projectDocs, loading, expanded } = entry;

  const totalCount = propertyDocs.length + projectDocs.reduce((s, pd) => s + pd.docs.length, 0);

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      {/* Property header row */}
      <div
        style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', cursor: 'pointer', gap: 12 }}
        onClick={onToggle}
        onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
        onMouseLeave={e => (e.currentTarget.style.background = '')}
      >
        <span style={{ color: '#94a3b8', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </span>
        <FolderOpen size={18} style={{ color: BRAND_GREEN, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
            {property.label || property.address1}
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 1 }}>
            {property.city}, {property.state}
          </div>
        </div>
        {expanded && totalCount > 0 && (
          <span style={{ fontSize: 12, background: '#f1f5f9', color: '#475569', padding: '3px 10px', borderRadius: 20, fontWeight: 600, flexShrink: 0 }}>
            {totalCount} doc{totalCount !== 1 ? 's' : ''}
          </span>
        )}
        <button
          onClick={e => { e.stopPropagation(); onNavigateToProperty(); }}
          style={{ fontSize: 12, color: '#64748b', background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', flexShrink: 0 }}
        >
          View Property
        </button>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div style={{ borderTop: '1px solid #f1f5f9' }}>
          {loading ? (
            <div style={{ padding: '20px 24px', color: '#94a3b8', fontSize: 13 }}>Loading documents…</div>
          ) : (
            <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Property-level docs */}
              <DocSection
                title="Property Documents"
                icon={<FileText size={15} />}
                docs={propertyDocs}
                emptyText="No property-level documents uploaded."
                onDelete={onDeletePropertyDoc}
                onDownload={onDownloadPropertyDoc}
                showDelete
              />

              {/* Per-project docs */}
              {projectDocs.length > 0 && projectDocs.map(({ project, docs }) => (
                <div key={project.id}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Project Docs</span>
                    <span style={{ fontSize: 12, color: '#64748b' }}>
                      {project.projectType.charAt(0) + project.projectType.slice(1).toLowerCase()} · {project.status.replace(/_/g, ' ')}
                    </span>
                    <button
                      onClick={() => onNavigateToProject(project.id)}
                      style={{ fontSize: 11, color: '#64748b', background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', marginLeft: 'auto' }}
                    >
                      Open Project
                    </button>
                  </div>
                  <DocSection
                    title=""
                    icon={<FileText size={15} />}
                    docs={docs}
                    emptyText="No documents for this project."
                    onDownload={onDownloadProjectDoc}
                    showDelete={false}
                  />
                </div>
              ))}

              {projectDocs.length === 0 && propertyDocs.length === 0 && (
                <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '12px 0' }}>
                  No documents found for this property or its projects.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface DocSectionProps {
  title: string;
  icon: React.ReactNode;
  docs: Doc[];
  emptyText: string;
  onDelete?: (docId: string) => void;
  onDownload?: (docId: string) => void;
  showDelete: boolean;
}

function DocSection({ title, docs, emptyText, onDelete, onDownload, showDelete }: DocSectionProps) {
  return (
    <div>
      {title && (
        <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
          {title}
        </div>
      )}
      {docs.length === 0 ? (
        <div style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>{emptyText}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {docs.map(doc => (
            <div
              key={doc.id}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #f1f5f9' }}
            >
              <FileText size={16} style={{ color: '#94a3b8', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {docLabel(doc)}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                  {doc.documentCategory || doc.documentType} · {fmtDate(doc.createdAt)}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {doc.s3Key && onDownload && (
                  <button
                    title="Download"
                    onClick={() => onDownload(doc.id)}
                    style={{ display: 'flex', alignItems: 'center', padding: '5px 8px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, color: '#475569', cursor: 'pointer' }}
                  >
                    <Download size={13} />
                  </button>
                )}
                {showDelete && onDelete && (
                  <button
                    onClick={() => onDelete(doc.id)}
                    title="Delete"
                    style={{ display: 'flex', alignItems: 'center', padding: '5px 8px', background: '#fff', border: '1px solid #fee2e2', borderRadius: 6, color: '#dc2626', cursor: 'pointer' }}
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
// CB:END pm-documents-page
